import {
  ButtonPrimary,
  ButtonSecondary,
  ButtonSize,
  ClickOutsideDetector,
  formatDate,
  IconDots,
  IconEdit,
  IconRefresh,
  IconSandWatch,
} from '@ui/src';
import {
  AmlAction,
  ClientScoreTypeEnum,
  CustomerScoreUpdateStatus,
  NewApplicationStatus,
} from '../../../../../hoc/common/types';
import { RiskScoreTopTypes } from '../types';

import cn from '../styles.module.css';

const RiskScoreTop = ({
  blockCollapsed,
  riskScoreIsLoading,
  aml,
  status,
  requestInProgress,
  setBlockCollapsed,
  expanded,
  setExpanded,
  riskScoreRanges,
  users,
  amlUpdateStatus,
  manualScoreAllowed,
  setManualScoreSidepanelVisible,
  calculateAllowed,
  setPopupVisible,
  changeLogsAvailable,
  setRiskScoreMenuVisible,
  riskScoreMenuVisible,
  setRiskScoreChangeLogsSidepanelVisible,
}: RiskScoreTopTypes) => {
  let totalRiskClassName: string;

  const riskLevel = riskScoreRanges?.find(
    (item) =>
      (aml?.totalRiskScore as number) >= item.rangeStart &&
      (aml?.totalRiskScore as number) <= item.rangeEnd,
  );

  if (riskLevel?.action === AmlAction.NONE) {
    totalRiskClassName = cn.totalRiskScoringGood;
  } else if (riskLevel?.action === AmlAction.OPS_REVIEW) {
    totalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    totalRiskClassName = cn.totalRiskScoringLow;
  }

  return (
    <div
      className={`${cn.newApplicationRiskScore} ${
        blockCollapsed ? cn.totalRiskScoringTopRounded : ''
      } ${
        !riskScoreIsLoading &&
        !aml &&
        status === NewApplicationStatus.INCOMPLETE &&
        requestInProgress
          ? cn.unfilled
          : ''
      }`}
    >
      <button
        type="button"
        aria-label="Collapse button"
        className={cn.totalRiskCollapseButton}
        onClick={() => {
          setBlockCollapsed(!blockCollapsed);
          if (!expanded) {
            setExpanded(true);
          } else if (expanded && blockCollapsed) {
            setExpanded(false);
          }
        }}
        disabled={Boolean(aml?.clientScoreType === ClientScoreTypeEnum.MANUAL)}
      />
      {!riskScoreIsLoading &&
        !aml &&
        status === NewApplicationStatus.INCOMPLETE && (
          <>
            <div className={cn.newApplicationRiskScoreIconWrapper}>
              <IconSandWatch />
            </div>
            <span className={cn.noRiskScoreText}>
              Customer Risk Score will be calculated after the client fills out
              the application
            </span>
          </>
        )}
      {riskScoreIsLoading && !aml && (
        <div className={cn.riskScoreTotalText}>
          Customer Application Risk Score is Loading...
        </div>
      )}
      {!riskScoreIsLoading &&
        !aml &&
        status !== NewApplicationStatus.INCOMPLETE && (
          <div className={cn.riskScoreTotalText}>
            Customer Application Risk Score is calculating...
          </div>
        )}
      <div className={cn.riskTotalWrapper}>
        <div className={cn.riskTotalLeft}>
          {aml && (
            <>
              <div
                className={`${cn.riskScoreTotalValue} ${totalRiskClassName}`}
              >
                {aml?.totalRiskScore}
              </div>
              <div
                className={`${cn.riskScoreLevel} ${totalRiskClassName}`}
              >{`${riskLevel?.name.toLowerCase() || 'High'} Risk`}</div>
              <div className={cn.totalRiskScoringType}>
                {aml?.clientScoreType === ClientScoreTypeEnum.AUTO ? (
                  <span>
                    <IconRefresh width={14} height={14} /> auto
                  </span>
                ) : (
                  <span>
                    <IconEdit width={14} height={14} /> manually
                  </span>
                )}
              </div>
              <div className={cn.totalRiskScoringDescription}>
                <div className={cn.totalRiskScoringCalculatedDate}>
                  {aml?.updatedAt || aml?.createdAt ? (
                    <span>
                      {formatDate(aml?.updatedAt || aml?.createdAt, true)}
                    </span>
                  ) : (
                    ''
                  )}
                </div>
                {aml?.employeeId && (
                  <>
                    <div className={cn.amlCircle} />
                    <div className={cn.riskScoringEmployee}>
                      {
                        users?.find((item) => item.value === aml?.employeeId)
                          ?.name
                      }
                    </div>
                  </>
                )}
              </div>
              {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && (
                <div className={cn.updateStatusFailed}>
                  Update Customer Application Risk Score Failed. Try again
                </div>
              )}
            </>
          )}
          {amlUpdateStatus === CustomerScoreUpdateStatus.FAIL && !aml && (
            <div className={cn.statusFailed}>
              Update Customer Application Risk Score Failed. Try again
            </div>
          )}
          {amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED && !aml && (
            <div className={cn.riskScoreTotalText}>
              The customer&apos;s risk score is calculating...
            </div>
          )}
        </div>
        <div className={cn.riskTotalRight}>
          {manualScoreAllowed &&
            status !== NewApplicationStatus.ACCEPTED &&
            status !== NewApplicationStatus.DECLINED && (
              <div className={cn.totalRiskScoringButtonWrapper}>
                <ButtonSecondary
                  size={ButtonSize.SMALL}
                  text="Set manually"
                  className={`button-white ${cn.setRiskScoreButton}`}
                  icon={<IconEdit />}
                  onClick={() => setManualScoreSidepanelVisible(true)}
                />
              </div>
            )}
          {calculateAllowed && (
            <div className={cn.totalRiskScoringButtonWrapper}>
              {status !== NewApplicationStatus.ACCEPTED &&
                status !== NewApplicationStatus.DECLINED && (
                  <ButtonPrimary
                    icon={
                      amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED ? (
                        <IconRefresh />
                      ) : undefined
                    }
                    size={ButtonSize.SMALL}
                    text={
                      amlUpdateStatus === CustomerScoreUpdateStatus.QUEUED
                        ? 'Updating...'
                        : 'Auto update'
                    }
                    className={cn.updateRiskScoreButton}
                    onClick={() =>
                      amlUpdateStatus !== CustomerScoreUpdateStatus.QUEUED &&
                      setPopupVisible(true)
                    }
                  />
                )}
            </div>
          )}
          {changeLogsAvailable && (
            <div className={cn.riskScoreMenuWrapper}>
              <ClickOutsideDetector
                setOptionsListVisible={setRiskScoreMenuVisible}
              >
                <>
                  <button
                    type="button"
                    className={`${cn.riskScoreMenuButton} ${
                      riskScoreMenuVisible ? cn.active : ''
                    }`}
                    onClick={() =>
                      setRiskScoreMenuVisible(!riskScoreMenuVisible)
                    }
                  >
                    <IconDots />
                  </button>
                  {riskScoreMenuVisible && (
                    <div className={cn.riskScoreMenu}>
                      <button
                        type="button"
                        className={cn.riskScoreMenuListButton}
                        onClick={() => {
                          setRiskScoreChangeLogsSidepanelVisible(true);
                          setRiskScoreMenuVisible(false);
                        }}
                      >
                        Change Log
                      </button>
                    </div>
                  )}
                  <div />
                </>
              </ClickOutsideDetector>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskScoreTop;
